import { Alert, Button, Card, Descriptions, Skeleton, Space, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canWritePacks } from '@/layouts/navigation';
import { usePack } from '../hooks/usePack';
import { PackStatusBadge } from '../components/PackBadges';
import { formatCop, formatDateTime, formatKwh } from '../utils/format';

/** Detalle de solo lectura de un Pack de Energía (`/packs/:packId`). */
export function PackDetailPage() {
  const { packId } = useParams<{ packId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { pack, loading, error, notFound, refresh } = usePack(packId);
  const canWrite = canWritePacks(user?.role);

  const goToList = () => navigate('/packs');

  return (
    <div className="packs-page">
      <div className="packs-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al listado de packs"
            onClick={goToList}
          >
            Volver
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            {pack?.name ?? 'Detalle del pack'}
          </Typography.Title>
        </Space>
        {canWrite && pack && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/packs/${encodeURIComponent(pack.id)}/editar`)}
          >
            Editar pack
          </Button>
        )}
      </div>

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Card>
      ) : notFound ? (
        <Alert
          type="warning"
          showIcon
          message="No encontramos el pack"
          description="Puede que se haya eliminado o que el enlace esté incompleto."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el pack"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : pack ? (
        <Card>
          <Descriptions column={{ xs: 1, sm: 2 }} size="middle">
            <Descriptions.Item label="Identificador">
              <Typography.Text style={{ fontFamily: 'var(--ge-font-mono)' }}>
                {pack.id}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              <PackStatusBadge status={pack.status} validTo={pack.validTo} />
            </Descriptions.Item>
            <Descriptions.Item label="Energía">{formatKwh(pack.kwh)}</Descriptions.Item>
            <Descriptions.Item label="Precio">
              {formatCop(pack.priceCop, pack.currency || 'COP')}
            </Descriptions.Item>
            <Descriptions.Item label="Moneda">{pack.currency || 'COP'}</Descriptions.Item>
            <Descriptions.Item label="Vigente desde">
              {formatDateTime(pack.validFrom)}
            </Descriptions.Item>
            <Descriptions.Item label="Vigente hasta">
              {pack.validTo ? formatDateTime(pack.validTo) : 'Sin fecha de fin'}
            </Descriptions.Item>
            <Descriptions.Item label="Creado">{formatDateTime(pack.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="Actualizado">
              {formatDateTime(pack.updatedAt)}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}
    </div>
  );
}
